import type { DownloadInformation } from '@egr/xbox/share/processor/ShareContentOWDSUploadProcessor';

import { getShareLabel } from '../share/ShareUtils';

import { owdsValidUntil } from '@egr/xbox/eaiws-plugins/utils';
import { getOWDSShareFileUrl } from '@egr/xbox/services/OWDS';
import { IgnoreAjaxErrorHandlerError } from '@egr/xbox/utils/AjaxErrorHandler';
import { loadHiddenFrame } from '@egr/xbox/utils/Download';
import { isExportGeometryFormat } from '@egr/xbox/utils/FileFormats';
import { disableUnloadChecks, setUnloadCheckDisabled } from '@egr/xbox/utils/Url';
import { ios } from '@egr/xbox/utils/UserAgent';
import { ErrorStack } from '@egr/xbox/utils/errors/Error';

export function isPlanning(value: DownloadInformation): boolean {
    const format = value.formatKey;

    return ['pbox', 'opl', 'planningUrl'].includes(format);
}

export function isArticleListShareInformation(value: DownloadInformation): boolean {
    const format = value.formatKey;
    return ['excel', 'articlelist'].includes(format);
}

export function isCADShareInformation(value: DownloadInformation): boolean {
    return isExportGeometryFormat(value.formatKey.toUpperCase())
         ||['GLBWITHWALLS', 'USDZWITHWALLS'].includes(value.formatKey.toUpperCase());
}

async function getAttachmentShareUris(attachments: Array<object>): Promise<Array<string>> {
    return Promise.all(attachments?.map((value, index, array) => {
        return getOWDSShareFileUrl({
            filename: 'sentry_error' + (array.length > 1 ? `(${index})` : '') + '.json',
            content: new Blob([JSON.stringify(value, null, 2)])
        });
     }) ?? []);
}

export async function sendMailInBrowser(body: string, recipient?: string, subject?: string, isHTML?: boolean, attachments?: Array<object>): Promise<void> {
    const unloadCheckBefore: boolean = disableUnloadChecks;

    setUnloadCheckDisabled(true);

    try {
        if (attachments?.length) {
            const uris: Array<string> = await getAttachmentShareUris(attachments);
            body += '\n';
            if (ErrorStack.errors.length > 0) {
                body += '\nError report:';
            }
            body += '\n' + uris.join('\n');
        }
    } catch (error) {
        if (!(error instanceof IgnoreAjaxErrorHandlerError)) {
            throw error;
        }
    } finally {
        let uri: string = 'mailto:';
        if (recipient) {
            uri += recipient;
        }

        if (subject) {
            uri += `?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        } else {
            uri += `?body=${encodeURIComponent(body)}`;
        }

        if (ios) {
            window.location.href = uri;
        } else {
            loadHiddenFrame(uri);
        }

        setUnloadCheckDisabled(unloadCheckBefore);
    }
}

export function prettyMailSubContent(previous: string, current: DownloadInformation): string {
    return `${previous}\r\n${getShareLabel(current)}: ${current.url} (${owdsValidUntil(current.expirationTimestamp, true)})\r\n`;
}
